import { LanguageResolutionSource } from '../services/i18n.service';

export interface WebappInitializationData {
    planName: string;
    offerName: string;
    isXero?: boolean;
    hasDiscountCode?: boolean;
    languageResolvedFrom: LanguageResolutionSource;
}

export enum ApplicationStatus {
    Undefined = 'Undefined',
    Pending = 'Pending',
    Completed = 'Completed',
    ReviewInProgress = 'ReviewInProgress',
    Finalized = 'Finalized',
    Abandoned = 'Abandoned'
}

export enum ApplicationSource {
    web = 'Web',
    direct = 'Direct',
    xero = 'Xero'
}

export enum ApplicationSegment {
    direct = 'Direct',
    selfServe = 'Self Serve'
}

export enum UBOProvisioningChoice {
    Online = 'Online',
    Email = 'Email',
    None = 'None'
}

export enum FsDistributor {
    Default = 'Default',
    Italian = 'Italian'
}

export type EMoneyLicenseIssuer = 'SFS-UK' | 'SFS-IRL';

export interface Applicant {
    contactId?: string;
    email: string;
    firstName: string;
    lastName: string;
}
export interface Address {
    postalCode?: string;
    city?: string;
    countryISO: string;
    province?: string;
    searchRefCode?: string;
    line1?: string;
    line2?: string;
}

export interface Company {
    tradingName: string;
    registeredName: string;
    type: string;
    typeDescription?: string;
    employees: number;
    registrationNumber: string;
    vatNumber?: string;
    fiscalCode?: string;
    registeredAddress: Address;
    tradingAddress: Address;
    eMoneyLicenseIssuer: EMoneyLicenseIssuer;
    kybProviderId?: string;
    website?: string;
}

export enum FatcaChoice {
    Yes = 'Yes',
    No = 'No',
    NoChoice = null
}

export interface ContactData {
    id?: string;
    email: string;
    firstName: string;
    lastName: string;
    mobilePhone?: string;
    mobilePhoneICC?: string;
    address: Address;
    gender?: "M" | "F";
    fiscalCode?: string;
    birthDate?: string;
    birthPlace?: string;
    birthProvince?: string;
    birthCountry?: string;
    citizenshipCountryISO?: string;
    fatcaSubject?: FatcaChoice;
}

export enum ContactRole {
    Admin = 'Admin',
    Applicant = 'Applicant',
    LegalRepresentative = 'LegalRepresentative',
    UltimateBeneficialOwner = 'UltimateBeneficialOwner'
  }

export interface Contact extends ContactData {
    id?: string;
    roles: ContactRole[];
}

export function addContactRole(contact: Contact, contactRole: ContactRole) {
    if (!contact.roles.includes(contactRole)) {
        contact.roles.push(contactRole);
    }
}

export function removeContactRole(contact: Contact, contactRole: ContactRole) {
    const index = contact.roles.indexOf(contactRole);
    if (index > -1) {
        contact.roles.splice(index, 1);
    }
}

export interface Application {
    id: string;
    applicant: Applicant;
    company: Company;
    status: ApplicationStatus;
    segment: ApplicationSegment;
    language: string;
    handover: boolean;
    offerName: string;
    planName: string;
    discountCode?: string;
    cookiePreferences: string;
    uboProvisioningChoice: UBOProvisioningChoice;
    webappCheckList: string;
    salesforceExternalId: string;
    distributor: FsDistributor;
    creationTime: string;
    submittedTime: string;
    firstTouchWebappVersion: string;
    lastTouchWebappVersion: string;
    firstTouchBackendVersion: string;
    lastTouchBackendVersion: string;
}

export interface MarketingData {
    affiliateData?: {
        id?: string;
        clickTime?: string;
        metadata?: {
            [property: string]: string;
        };
    };
    googleData?: {
        clickId?: string;
        clickTimestamp?: string;
        analyticsClientId?: string;
    };
    utmData?: {
        source?: string;
        medium?: string;
        campaign?: string;
        term?: string;
        content?: string;
    };
    marketoCookieId?: string;
    driverId?: string;
    destinationId?: string;
}

export enum SalesChannel {
    ONLINE = "Online",
    RETAIL = "Retail",
    OTHER = "Other"
}

export interface BusinessActivitySurvey {
    salesChannel: string;
    salesChannelDescription: string;
    internationalActivity: boolean;
    internationalActivityPrimaryCountry: string;
    businessDescription: string;
}

export interface ExpectedActivitySurvey {
    reasonOfUse: string;
    reasonOfUseDescription: string;
    numberOfCards: number;
    monthlySpend: number;
    monthlyCashWithdrawal: number;
    monthlyCashWithdrawalReason: string;
    thirdPartyMoneyLoad: number;
    thirdPartyMoneyLoadDescription: string;
}

export interface IDVData {
    idvReference: string;
    identityVerificationSteps?: string;
    contactId?: string;
    kycPass?: boolean;
    personExternalId?: string;
    providerName: string;
}

export enum ComplianceStatus {
    DOC_REQUIRED = 'DOC_REQUIRED',
    KYB_PENDING = 'KYB_PENDING',
    COMPANY_PROVISIONED = 'COMPANY_PROVISIONED',
    IDV_REQUIRED = 'IDV_REQUIRED',
    MANUAL_REVIEW_REQUIRED = 'MANUAL_REVIEW_REQUIRED',
    DOTFILE_KYB_REQUIRED = 'DOTFILE_KYB_REQUIRED'
}

export interface ComplianceData {
    termsAndConditions: boolean;
    idvData: IDVData[];
    status: ComplianceStatus;
    inEarlyAccessPhase?: boolean;
}

export interface EpaStatus {
    earlyAccessPhaseCandidate: boolean;
    earlyAccessPlanName: string;
}

export interface EmailVerificationData {
    email: string,
    firstName?: string,
    lastName?: string,
    lang: string,
    countryIso3?: string
}

export interface CheckVerificationCodeRequestDTO {
    email: string;
    code: string;
}

export interface ApplicationStartDTO {
    email: string;
    browserLanguage: string;
    cookiePreferences: string;
    planName: string;
    offerName: string;
    webappCheckList: string;
    discountCode?: string;
    sourceOverride?: ApplicationSource;
}

export interface CompanySearchElement {
    name: string;
    status: string;
    kybProviderId: string;
    registrationNumber: string;
    addressSummary: string;
}

export interface CompanySearchResult {
    companies: CompanySearchElement[];
    totalResults: number;
}

export interface CompanyLookupResult {
    name: string,
    status: string,
    type: string,
    kybProviderId: string,
    registrationNumber: string,
    vatNumber: string,
    dateOfIncorporation: string,
    employees: number,
    registeredAddress: {
      line1: string,
      line2: string,
      city: string,
      postalCode: string,
      province: string,
      country: string
    }
  }

export interface AddressSearchResult {
    addressId: string;
    text: string;
}

export interface XeroOAuthDTO {
    state: string;
    reference: string;
    clientId?: string;
    exchangeCode?: string;
    redirectUri: string;
}

export interface CompanyType {
    value: string;
    label: string;
    category: 'REGISTERED_BUSINESS' | 'OTHER_ENTITIES';
}

export interface ItalianProvince  {
    code: string;
    region: string;
    principal: string;
}

export interface ItalianTown {
    taxCode: string;
    town: string;
    region: string;
    province: string;
}

export interface Town {
    name: string;
    province: string;
}

export interface CountryMetadata {
    name?: string;
    iso2: string;
    iso3: string;
    numeric: string;
    icc: string;
    activeMarket: boolean;
    lang: string;
    blacklist: boolean;
    eMoneyLicenseIssuer: string;
    dotfileSearchEnabled?: boolean;
}

export interface CountryName {
    iso3: string;
    name: string;
    alt: string;
}

// 'unmapped': the geolocation endpoint answered with a country we have no metadata for
export type GeolocationOutcome = 'resolved' | 'cached' | 'unmapped' | 'failed';

export interface GeolocationResult {
    countryIso3: string | null;
    outcome: GeolocationOutcome;
}

export interface UserIDVDetails {
    address: {
        postalCode: string,
        city: string,
        countryISO: string,
        province: string,
        searchRefCode: string,
        line1: string,
        line2: string
    },
    fiscalCode: string,
    birthDate: string,
    birthPlace: string,
    birthProvince: string,
    nationality: string
}

export interface IDVSessionDetails {
    userKYCRequestId: string,
    workflowRunId?: string,
    identityVerificationStatus?: "ABANDONED" | "COMPLETED" | "SENT",
    firstName?: string,
    middleName?: string,
    lastName?: string,
    companyName?: string
}

export interface BundleInfo {
    plan: string;
    offer: string;
    description?: string;
}

export const DEFAULT_STRING_MAX_LENGTH = 255;

export const APPLICATION_DEFAULT_FIELD_LENGTH: Partial<{ [K in keyof Application]: number }> = {
    language: 2
};

export const COMPANY_DEFAULT_FIELD_LENGTH: Partial<{ [K in keyof Company]: number }> = {
};

export const CONTACT_DEFAULT_FIELD_LENGTH: Partial<{ [K in keyof ContactData]: number }> = {
    email: 80,
    firstName: 40,
    lastName: 80,
    birthPlace: 80,
    birthProvince: 80,
    fiscalCode: 80,
    mobilePhoneICC: 80
};

export const ADDRESS_DEFAULT_FIELD_LENGTH: Partial<{ [K in keyof Address]: number }> = {
    city: 40,
    postalCode: 20,
    searchRefCode: 80,
    province: 80
};

export const BUSINESS_ACTIVITY_SURVEY_FIELD_LENGTH: Partial<{ [K in keyof BusinessActivitySurvey]: number}> = {

}

export const EXPECTED_ACTIVITY_SURVEY_FIELD_LENGTH: Partial<{ [K in keyof ExpectedActivitySurvey]: number}> = {

}
